import React, { useCallback, useEffect, useState } from "react";
import Customers from "../components/cards/Customers";
import DuePayable from "../components/cards/DuePayable";
import AvailableStock from "../components/cards/AvailableStock";
import Revenue from "../components/cards/Revenue";
import YearlySales from "../components/cards/YearlySales";
import Orders from "../components/cards/Orders";
import Visitors from "../components/cards/Visitors";
import Banks from "../components/cards/Banks";
import UntaggedPool from "../components/cards/UntaggedPool";
import { isErpApiSuccess, postDashboard } from "../erpClient";
import { createErpSocket } from "../utils/socketIo";
import {
  ERP_DASHBOARD_POOL_INVALIDATE_EVENT,
  isSocketForCurrentBranch,
} from "../utils/socketPoolEvents";
import {
  getCurrentPinType,
  getQuoteInvoiceListScope,
  QUOTE_INVOICE_LIST_SCOPE_EVENT,
} from "./const";

let dashboardFetchPromise = null;
let dashboardCache = null;
let dashboardCacheTime = 0;
let dashboardCacheKey = "";
const DASHBOARD_DEDUPE_MS = 2000;

/** Quote mode + navbar "All" → type mixed; otherwise keep type all. */
function resolveDashboardRequestType() {
  const pin = String(getCurrentPinType() || "").toLowerCase();
  if (pin === "quote" && getQuoteInvoiceListScope() === "all") {
    return "mixed";
  }
  return "all";
}

function invalidateDashboardCache() {
  dashboardCache = null;
  dashboardCacheTime = 0;
  dashboardFetchPromise = null;
  dashboardCacheKey = "";
}

async function requestDashboard({ force = false } = {}) {
  const now = Date.now();
  const listType = resolveDashboardRequestType();
  const cacheKey = `${String(getCurrentPinType() || "access")}:${listType}`;

  if (!force) {
    if (dashboardFetchPromise && dashboardCacheKey === cacheKey) {
      return dashboardFetchPromise;
    }
    if (
      dashboardCache &&
      dashboardCacheKey === cacheKey &&
      now - dashboardCacheTime < DASHBOARD_DEDUPE_MS
    ) {
      return dashboardCache;
    }
  } else {
    invalidateDashboardCache();
  }

  dashboardCacheKey = cacheKey;
  dashboardFetchPromise = postDashboard({ type: listType })
    .then((res) => {
      if (isErpApiSuccess(res)) {
        dashboardCache = res;
        dashboardCacheTime = Date.now();
        dashboardCacheKey = cacheKey;
      }
      return res;
    })
    .finally(() => {
      dashboardFetchPromise = null;
    });

  return dashboardFetchPromise;
}

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboard = useCallback(async ({ force = false } = {}) => {
    try {
      const res = await requestDashboard({ force });
      if (isErpApiSuccess(res)) {
        setDashboardData(res?.data || null);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const handleApiUrlChange = () => {
      fetchDashboard({ force: true });
    };
    const handleQuoteScopeChange = () => {
      fetchDashboard({ force: true });
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    window.addEventListener(
      ERP_DASHBOARD_POOL_INVALIDATE_EVENT,
      handleApiUrlChange,
    );
    window.addEventListener(
      QUOTE_INVOICE_LIST_SCOPE_EVENT,
      handleQuoteScopeChange,
    );

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
      window.removeEventListener(
        ERP_DASHBOARD_POOL_INVALIDATE_EVENT,
        handleApiUrlChange,
      );
      window.removeEventListener(
        QUOTE_INVOICE_LIST_SCOPE_EVENT,
        handleQuoteScopeChange,
      );
    };
  }, [fetchDashboard]);

  useEffect(() => {
    const socket = createErpSocket();

    const refreshPool = (payload) => {
      if (!isSocketForCurrentBranch(payload)) return;
      fetchDashboard({ force: true });
    };

    socket.on("metal_transfer_created", refreshPool);
    socket.on("tag_cancelled", refreshPool);

    return () => {
      socket.off("metal_transfer_created", refreshPool);
      socket.off("tag_cancelled", refreshPool);
      socket.disconnect();
    };
  }, [fetchDashboard]);

  return (
    <div className="body-wrapper">
      <div className="container-fluid py-4">
        <div className="row g-4 align-items-start mt-1">
          <AvailableStock className="mt-0" stockData={dashboardData?.stock} />
          <YearlySales className="mt-0" salesData={dashboardData?.sales} />
          <Revenue className="mt-0" revenueData={dashboardData?.revenue} />
        </div>

        <div className="row g-4" style={{ marginTop: "-290px" }}>
          <Orders className="mt-0" ordersData={dashboardData?.orders} />
          <Visitors className="mt-0" visitorsData={dashboardData?.visitors} />
        </div>

        <div className="row g-4 mt-0 align-items-start">
          <Customers className="mt-0" customersData={dashboardData?.customers} />
          <DuePayable
            className="mt-0"
            duePayableData={dashboardData?.due_and_payable}
          />
          <Banks
            className="mt-0"
            bankBalancesData={dashboardData?.bank_balances}
          />
          <UntaggedPool className="mt-0" stockData={dashboardData?.stock} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
